
import React from 'react'
import { BarChart3, PieChart, TrendingUp, Calendar } from 'lucide-react'

interface ChartData {
  fluxoCaixaMensal: Array<{
    mes: string
    entradas: number
    saidas: number
    saldo: number
  }>
  evolucaoPatrimonial: Array<{
    mes: string
    patrimonio: number
  }>
  distribuicaoGastos: Array<{
    categoria: string
    valor: number
    percentual: number
  }>
  previsaoRecebimentos: Array<{
    data: string
    valor: number
    cliente: string
  }>
}

interface FinanceiroChartsProps {
  data: ChartData
}

export const FinanceiroCharts: React.FC<FinanceiroChartsProps> = ({ data }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const getCategoriaColor = (categoria: string) => {
    const colors: Record<string, string> = {
      'mao_obra': 'bg-blue-500',
      'material': 'bg-green-500',
      'administrativo': 'bg-yellow-500',
      'tributario': 'bg-red-500',
      'equipamentos': 'bg-purple-500',
      'financeiro': 'bg-indigo-500'
    }
    return colors[categoria] || 'bg-gray-500'
  }

  const getCategoriaLabel = (categoria: string) => {
    const labels: Record<string, string> = {
      'mao_obra': 'Mão de Obra',
      'material': 'Material',
      'administrativo': 'Administrativo',
      'tributario': 'Tributário',
      'equipamentos': 'Equipamentos',
      'financeiro': 'Financeiro'
    }
    return labels[categoria] || categoria
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Fluxo de Caixa Mensal */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold">Fluxo de Caixa Mensal</h3>
        </div>
        
        <div className="space-y-4">
          {data.fluxoCaixaMensal.map((item, index) => {
            const maxValue = Math.max(
              ...data.fluxoCaixaMensal.map(d => Math.max(d.entradas, d.saidas))
            )
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{item.mes}</span>
                  <span className={`font-medium ${
                    item.saldo >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(item.saldo)}
                  </span>
                </div>
                
                <div className="space-y-1">
                  {/* Entradas */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-green-600 w-16">Entradas</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${(item.entradas / maxValue) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-20 text-right">
                      {formatCurrency(item.entradas)}
                    </span>
                  </div>
                  
                  {/* Saídas */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 w-16">Saídas</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full"
                        style={{ width: `${(item.saidas / maxValue) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium w-20 text-right">
                      {formatCurrency(item.saidas)}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Evolução Patrimonial */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="text-green-600" size={20} />
          <h3 className="text-lg font-semibold">Evolução Patrimonial</h3>
        </div>
        
        <div className="space-y-3">
          {data.evolucaoPatrimonial.map((item, index) => {
            const maxPatrimonio = Math.max(...data.evolucaoPatrimonial.map(d => d.patrimonio))
            const isPositive = item.patrimonio >= 0
            
            return (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium w-16">{item.mes}</span>
                <div className="flex-1 mx-4">
                  <div className="bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full ${
                        isPositive ? 'bg-green-500' : 'bg-red-500'
                      }`}
                      style={{ 
                        width: `${Math.abs(item.patrimonio) / Math.abs(maxPatrimonio) * 100}%` 
                      }}
                    />
                  </div>
                </div>
                <span className={`text-sm font-medium w-24 text-right ${
                  isPositive ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatCurrency(item.patrimonio)}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* Distribuição de Gastos */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <PieChart className="text-purple-600" size={20} />
          <h3 className="text-lg font-semibold">Distribuição de Gastos</h3>
        </div>
        
        <div className="space-y-3">
          {data.distribuicaoGastos.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  {getCategoriaLabel(item.categoria)}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {item.percentual.toFixed(1)}%
                  </span>
                  <span className="text-sm font-medium">
                    {formatCurrency(item.valor)}
                  </span>
                </div>
              </div>
              <div className="bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getCategoriaColor(item.categoria)}`}
                  style={{ width: `${item.percentual}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Previsão de Recebimentos */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="text-indigo-600" size={20} />
          <h3 className="text-lg font-semibold">Previsão de Recebimentos</h3>
        </div>
        
        <div className="space-y-3">
          {data.previsaoRecebimentos.slice(0, 5).map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">{item.cliente}</p>
                <p className="text-xs text-gray-600">
                  {new Date(item.data).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <span className="text-sm font-semibold text-green-600">
                {formatCurrency(item.valor)}
              </span>
            </div>
          ))}
          
          {data.previsaoRecebimentos.length > 5 && (
            <div className="text-center pt-2">
              <span className="text-xs text-gray-500">
                +{data.previsaoRecebimentos.length - 5} recebimentos adicionais
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FinanceiroCharts
